import { useState } from "react";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { useSession } from "@/lib/auth";
import { useWorkspace } from "@/lib/workspace";
import {
  createFolder,
  renameFolder,
  removeFolder,
  organizeFile,
  starFile,
  useFileTrash,
  restoreFile,
  purgeFile,
  type LibraryFolder,
  type TrashFile,
} from "@/lib/file-library";
import type { FileEntry } from "@/lib/types";
import { formatBytes } from "@/lib/format";

export function FolderEditor({
  folder,
  parentId,
  roomId,
  onClose,
  onSaved,
}: {
  folder?: LibraryFolder;
  parentId?: string;
  roomId?: string;
  onClose: () => void;
  onSaved: (id?: string) => void;
}) {
  const [name, setName] = useState(folder?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function save() {
    setBusy(true);
    setError("");
    try {
      const id = folder
        ? (await renameFolder(folder.id, name), folder.id)
        : (await createFolder(name, parentId, roomId)).id;
      onSaved(id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "保存できませんでした");
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    setBusy(true);
    setError("");
    try {
      await removeFolder(folder!.id);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "削除できませんでした");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open
      title={folder ? "フォルダーの設定" : "フォルダーを作成"}
      onClose={() => {
        if (!busy) onClose();
      }}
      size="sm"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        className="space-y-4"
      >
        <label className="block text-sm">
          フォルダー名
          <Input
            autoFocus
            required
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={onClose}
          >
            キャンセル
          </Button>
          <Button type="submit" disabled={busy || !name.trim()}>
            {busy ? "保存中…" : "保存"}
          </Button>
        </div>
        {folder && (
          <div className="border-t border-border-subtle pt-3">
            <p className="mb-2 text-xs text-fg-secondary">
              フォルダーを削除しても、中のファイルと子フォルダーは残ります。
            </p>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => void remove()}
            >
              フォルダーのみ削除
            </Button>
          </div>
        )}
      </form>
    </Modal>
  );
}
export function FileOrganizationDialog({
  file,
  folders,
  starred,
  onClose,
}: {
  file: FileEntry;
  folders: LibraryFolder[];
  starred: boolean;
  onClose: () => void;
}) {
  const session = useSession();
  const workspace = useWorkspace();
  const canEdit =
    session?.user.id === file.uploaderId ||
    ["owner", "admin"].includes(workspace.data?.role ?? "");
  const [name, setName] = useState(file.name);
  const [folder, setFolder] = useState(file.folderId ?? "");
  const [star, setStar] = useState(starred);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function save() {
    setBusy(true);
    setError("");
    try {
      if (canEdit && (name !== file.name || folder !== (file.folderId ?? "")))
        await organizeFile(file.id, { name, folder_id: folder || null });
      if (star !== starred) await starFile(file.id, star);
      onClose();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "ファイルの設定を保存できませんでした",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      open
      title="ファイルを整理"
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        className="space-y-4"
      >
        <label className="block text-sm">
          ファイル名
          <Input
            required
            maxLength={100}
            disabled={!canEdit || busy}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          フォルダー
          <select
            aria-label="移動先フォルダー"
            disabled={!canEdit || busy}
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
            className="mt-1 w-full rounded border border-border-default bg-canvas p-2"
          >
            <option value="">未整理</option>
            {folders
              .filter((f) => !f.room_id || f.room_id === file.roomId)
              .map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={star}
            disabled={busy}
            onChange={(e) => setStar(e.target.checked)}
          />
          お気に入りに追加
        </label>
        {!canEdit && (
          <p className="text-xs text-fg-secondary">
            名前とフォルダーを変更できるのは、アップロードした本人または管理者です。
          </p>
        )}
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={onClose}
          >
            キャンセル
          </Button>
          <Button type="submit" disabled={busy || !name.trim()}>
            {busy ? "保存中…" : "保存"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
export function FileTrashView() {
  const query = useFileTrash();
  const [confirm, setConfirm] = useState<TrashFile | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  async function act(file: TrashFile, permanent = false) {
    setBusy(file.id);
    setError("");
    try {
      if (permanent) {
        await purgeFile(file.id);
        setConfirm(null);
      } else await restoreFile(file.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "処理できませんでした");
    } finally {
      setBusy(null);
    }
  }
  return (
    <section aria-label="ファイルのごみ箱" className="space-y-3">
      <h2 className="font-semibold">ごみ箱</h2>
      <p className="text-sm text-fg-secondary">
        復元すると元の場所に戻ります。完全削除するとファイル本体も削除され、元に戻せません。
      </p>
      {(error || query.error) && (
        <p role="alert" className="text-sm text-danger">
          {error || query.error?.message}
        </p>
      )}
      {query.isPending ? (
        <p role="status">読み込んでいます…</p>
      ) : query.data?.length === 0 ? (
        <p className="py-8 text-sm text-fg-muted">ごみ箱は空です</p>
      ) : (
        <ul className="divide-y divide-border-subtle">
          {query.data?.map((file) => (
            <li
              key={file.id}
              className="flex flex-wrap items-center gap-3 py-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{file.name}</p>
                <p className="text-xs text-fg-muted">
                  {formatBytes(file.size_bytes)}
                </p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                disabled={!!busy || !!file.purge_started_at}
                onClick={() => void act(file)}
              >
                復元
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={!!busy}
                onClick={() => setConfirm(file)}
              >
                完全削除
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Modal
        open={!!confirm}
        title="ファイルを完全に削除"
        onClose={() => {
          if (!busy) setConfirm(null);
        }}
        size="sm"
      >
        <p className="mb-4 text-sm">
          「{confirm?.name}」を完全に削除します。この操作は取り消せません。
        </p>
        {error && (
          <p role="alert" className="mb-3 text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button
            variant="secondary"
            disabled={!!busy}
            onClick={() => setConfirm(null)}
          >
            キャンセル
          </Button>
          <Button
            disabled={!!busy}
            onClick={() => {
              if (confirm) void act(confirm, true);
            }}
          >
            {busy ? "削除中…" : "完全に削除"}
          </Button>
        </div>
      </Modal>
    </section>
  );
}
